// Doc-Ref: docs/features/collaborations-current.md
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from "components/ui/tooltip";
import { Alert, AlertTitle, AlertDescription } from "components/ui/alert";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "components/ui/chart";
import { Info, Lightbulb, ArrowUp, ArrowDown, AlertCircle, Loader2 } from "lucide-react";
import { SocialImpactCard, SdgDetailDrawer } from "Explore/Components/SocialImpact";
import { getGeneralSocialImpactDetail } from "../../../../Services/Collaborations";
import { buildGeneralCollaborationsParams } from "./utils";
import useGeneralSocialImpact from "./hooks/useGeneralSocialImpact";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend,
  LabelList,
} from "recharts";
import styles from "./TabGeneral.module.scss";
import { getGeneralOverview } from "../../../../Services/Collaborations";
import { getRegionLabel } from "Explore/Config/regionData";
import InsufficientDataNotice from "../../Components/InsufficientDataNotice/InsufficientDataNotice";

const CustomLegend = (props) => {
  const { payload, mixData } = props;

  // Sort legend by level (smallest scope to largest)
  const levelOrder = {
    "Single Author": 1,
    "Institutional": 2,
    "National": 3,
    "International": 4,
  };

  const sortedPayload = [...payload].sort((a, b) => (levelOrder[b.value] || 0) - (levelOrder[a.value] || 0));

  return (
    <ul style={{ listStyle: 'none', padding: 0, margin: 0, marginLeft: 20 }}>
      {sortedPayload.map((entry, index) => {
        const dataItem = (mixData || []).find(d => d.name === entry.value);
        return (
          <li key={`item-${index}`} style={{ display: 'flex', alignItems: 'center', marginBottom: 8, fontSize: 13 }}>
            <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', backgroundColor: entry.color, marginRight: 8 }}></span>
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span style={{ cursor: 'help', color: '#475569' }}>{entry.value}</span>
                </TooltipTrigger>
                <TooltipContent side="right">{dataItem?.description}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </li>
        );
      })}
    </ul>
  );
};

export const CustomPieTooltip = ({ active, payload, total, metricLabel }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const value = data.value || 0;
    const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;

    return (
      <div style={{ backgroundColor: '#fff', border: '1px solid var(--light-grey, #e8e8e8)', padding: '12px', fontSize: '13px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
        <div style={{ fontWeight: '500', marginBottom: '4px', color: 'var(--text-1, #1a1a2e)' }}>{data.name}</div>
        <div style={{ color: 'var(--text-2, #6b7280)' }}>{metricLabel}: <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace', color: 'var(--explore-color, #0f3460)' }}>{value}</span>{total > 0 ? ` (${percentage}%)` : ""}</div>
      </div>
    );
  }
  return null;
};

const TabGeneral = ({
  filters = {},
  setFilters,
  onDataEmpty,
  entity,
  isActive = false,
  overviewData: propOverviewData,
  isOverviewLoading: propIsOverviewLoading,
  overviewError: propOverviewError,
  socialImpactData: propSocialImpactData,
  isLoadingSocialImpact: propIsLoadingSocialImpact,
  socialImpactError: propSocialImpactError,
  refetchSocialImpact: propRefetchSocialImpact,
}) => {
  const isControlledOverview = propOverviewData !== undefined || propIsOverviewLoading !== undefined;
  const isControlledSocialImpact = propSocialImpactData !== undefined || propIsLoadingSocialImpact !== undefined;

  const [internalData, setInternalData] = useState(null);
  const [internalLoading, setInternalLoading] = useState(true);
  const [internalError, setInternalError] = useState(null);

  const data = isControlledOverview ? (propOverviewData ?? null) : internalData;
  const loading = isControlledOverview ? Boolean(propIsOverviewLoading) : internalLoading;
  const error = isControlledOverview
    ? (propOverviewError?.message || (typeof propOverviewError === "string" ? propOverviewError : null))
    : internalError;

  const lastFetchedParams = useRef(null);

  const hasNoKpiValue = !data?.kpis || data.kpis.length === 0 || data.kpis.every(kpi => !kpi.value || kpi.value === 0 || kpi.value === "0");
  const hasNoScopeData = !data?.scope_data || data.scope_data.length === 0 || data.scope_data.every(s => !s.value || s.value === 0 || s.value === "0");
  const hasNoReachData = !data?.reach_data || data.reach_data.length === 0 || data.reach_data.every(r => !r.value || r.value === 0 || r.value === "0");

  const params = useMemo(() => buildGeneralCollaborationsParams(entity, filters), [entity, filters]);
  const isStale = !isControlledOverview && lastFetchedParams.current !== JSON.stringify(params);

  const [selectedSdg, setSelectedSdg] = useState(null);

  // Close drawer if filters or entity change
  useEffect(() => {
    setSelectedSdg(null);
  }, [filters, entity]);

  const isInstitution = !entity?.type || (entity?.type).toLowerCase() === "institution";

  const hookSocialImpact = useGeneralSocialImpact(params, isActive && !isControlledSocialImpact, isInstitution);

  const socialImpactData = isControlledSocialImpact ? propSocialImpactData : hookSocialImpact.data;
  const isLoadingSocialImpact = isControlledSocialImpact ? Boolean(propIsLoadingSocialImpact) : hookSocialImpact.isLoading;
  const socialImpactError = isControlledSocialImpact ? propSocialImpactError : hookSocialImpact.error;
  const refetchSocialImpact = isControlledSocialImpact ? propRefetchSocialImpact : hookSocialImpact.refetch;

  const fetchSocialImpactDetail = useCallback((sdgNum, signal) => {
    return getGeneralSocialImpactDetail(sdgNum, params, { signal });
  }, [params]);

  const isMainDataEmpty = !isStale && !loading && !error && (!data || (hasNoKpiValue && hasNoScopeData && hasNoReachData));

  useEffect(() => {
    if (onDataEmpty && isActive) {
      onDataEmpty(isMainDataEmpty);
    }
  }, [isMainDataEmpty, onDataEmpty, isActive]);

  useEffect(() => {
    if (isControlledOverview) return;
    if (!entity || !isActive) return;

    const paramsString = JSON.stringify(params);
    if (lastFetchedParams.current === paramsString) {
      return;
    }
    lastFetchedParams.current = paramsString;

    const fetchOverview = async () => {
      setInternalLoading(true);
      setInternalError(null);
      try {
        const res = await getGeneralOverview(params);
        setInternalData(res);
      } catch (err) {
        lastFetchedParams.current = null;
        console.error("Failed to fetch general overview:", err);
        setInternalError(err.message || "Something went wrong.");
      } finally {
        setInternalLoading(false);
      }
    };

    fetchOverview();
  }, [filters, entity, isActive, isControlledOverview, params]);

  useEffect(() => {
    if (!loading && data && isActive) {
      requestAnimationFrame(() => {
        const mainEl = document.querySelector("main");
        if (mainEl && mainEl.scrollTop > 0) {
          mainEl.scrollTop = 0;
        }
        window.scrollTo(0, 0);
      });
    }
  }, [loading, data, isActive]);

  const metric = filters?.metric || "publications";

  const metricConfig = {
    publications: { label: "Publications", dataKey: "pubs", color: "#0f3460" },
    fwci: { label: "FWCI", dataKey: "fwci", color: "#1e5699" },
    citations: { label: "Citations", dataKey: "citations", color: "#3a86ff" },
    collaborators: { label: "Collaborators", dataKey: "collaborators", color: "#64748b" },
  };

  const currentMetric = metricConfig[metric] || metricConfig.publications;

  if (loading) {
    return (
      <div className="w-full flex justify-center items-center py-20 min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles["tab-general"]}>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!data) {
    return (
      <div className={styles["tab-general"]}>
        <Alert>
          <Info className="h-4 w-4" />
          <AlertTitle>No Data</AlertTitle>
          <AlertDescription>No overview data available.</AlertDescription>
        </Alert>
      </div>
    );
  }

  const {
    kpis = [],
    insights = [],
  } = data;
  const scopeData = data.scope_data || [];
  const trendData = data.trend_data || [];
  const reachData = data.reach_data || [];
  const rawSubjectAreaData = data.subject_area_data || [];

  const MIX_COLORS = {
    "International Only": { color: "#0f3460", description: "Publications with co-authors from other countries exclusively" },
    "US & International": { color: "#1e5699", description: "Publications with co-authors from both the US and other countries" },
    "US Only": { color: "#3a86ff", description: "Publications with co-authors exclusively from the US" },
    "International": { color: "#0f3460", description: "Publications with co-authors from other countries" },
    "US": { color: "#1e5699", description: "Publications with co-authors from the US" },
    "National": { color: "#3a86ff", description: "Publications with co-authors from the same country" },
    "Institutional": { color: "#64748b", description: "Publications with co-authors from the same institution" },
    "Single Author": { color: "#cbd5e1", description: "Publications with only one author" },
  };

  const enrichedScopeData = scopeData.map(item => ({
    ...item,
    name: item.label,
    color: MIX_COLORS[item.label]?.color || "#cccccc",
    description: MIX_COLORS[item.label]?.description || "",
  }));

  const scopeTotal = enrichedScopeData.reduce((acc, curr) => acc + (curr.value || 0), 0);

  const opacities = ["ff", "cc", "99", "66", "33"];

  const enrichedReachData = reachData.map((item, idx) => ({
    ...item,
    label: getRegionLabel(item.label, filters?.geography),
    fill: `${currentMetric.color}${opacities[idx % opacities.length]}`
  }));

  const subjectAreaData = rawSubjectAreaData.map((item, idx) => ({
    ...item,
    fill: `${currentMetric.color}${opacities[idx % opacities.length]}`
  }));

  const chartConfig = {
    value: {
      label: currentMetric.label,
      color: currentMetric.color,
    },
  };

  const splitYear = 2024;
  let splitPercent = 100;
  if (trendData.length > 1) {
    const splitIndex = trendData.findIndex(d => parseInt(d.year, 10) === splitYear);
    if (splitIndex !== -1) {
      splitPercent = (splitIndex / (trendData.length - 1)) * 100;
    }
  }

  const scopeChartConfig = enrichedScopeData.reduce((acc, item) => {
    acc[item.name] = { label: item.name, color: item.color };
    return acc;
  }, {});

  if (isMainDataEmpty && filters?.subjectArea?.length > 0) {
    return null;
  }

  // Pub count for low-data detection (use first KPI value — "Total Publications")
  const LOW_PUB_THRESHOLD = 10;
  const pubKpi = kpis.find(k =>
    (k.label || k.name || "").toLowerCase().includes("publication")
  ) || kpis[0];
  const rawTotalPubs = pubKpi?.value ?? 0;
  const parsedPubs = typeof rawTotalPubs === 'string'
    ? parseInt(rawTotalPubs.replace(/,/g, ''), 10)
    : Number(rawTotalPubs);
  const totalPubs = isNaN(parsedPubs) ? 0 : parsedPubs;
  const isLowData = !loading && totalPubs < LOW_PUB_THRESHOLD;

  return (
    <div className={styles["tab-general"]}>
      {/* Intro */}
      <div className={styles["tab-general__intro"]}>
        <p>
          This overview summarizes the high-level collaboration profile for the selected entity.
          Use it to understand collaboration scale, geographic reach, subject strengths, and output
          impact before diving into specific partnerships.
        </p>
      </div>

      {/* Unified Insufficient Data Notice when publication < 10 */}
      {isLowData ? (
        <InsufficientDataNotice
          pubCount={totalPubs}
          entity={entity}
          filters={filters}
          setFilters={setFilters}
        />
      ) : (
        <>
          {/* KPI Row */}
          {kpis.length > 0 && (
            <div className={styles["kpi-row"]}>
              {kpis.map((kpi, idx) => (
                <div key={idx} className={styles["kpi-item"]}>
                  {/* Label row */}
                  <div className={styles["kpi-item__label"]}>
                    <span className={styles["kpi-item__label-text"]}>
                      {kpi.label || kpi.name}
                    </span>
                    {kpi.help && (
                      <TooltipProvider delayDuration={0}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="h-3 w-3 text-gray-400 ml-1 cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent>{kpi.help}</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>

                  {/* Primary value */}
                  <div className={styles["kpi-item__value"]}>
                    {kpi.value}
                    {kpi.unit && (
                      <span className={styles["kpi-item__unit"]}>{kpi.unit}</span>
                    )}
                  </div>

                  {/* Trend badge */}
                  {typeof kpi.percentChange === 'number' ? (() => {
                    const yearStart = filters?.yearRange?.[0];
                    const yearEnd = filters?.yearRange?.[1];
                    const tooltipTitle = (yearStart && yearEnd && kpi.startValue != null && kpi.endValue != null)
                      ? `${yearStart}: ${kpi.startValue} → ${yearEnd}: ${kpi.endValue} (${kpi.delta > 0 ? '+' : ''}${kpi.delta})`
                      : undefined;
                    return (
                      <TooltipProvider delayDuration={0}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className={styles["kpi-item__footer"]}>
                              <span
                                className={`${styles["kpi-item__badge"]} ${kpi.percentChange > 0
                                  ? styles["kpi-item__badge--up"]
                                  : kpi.percentChange < 0
                                    ? styles["kpi-item__badge--down"]
                                    : styles["kpi-item__badge--neutral"]
                                  }`}
                              >
                                {kpi.percentChange > 0 ? <ArrowUp className="h-3 w-3" /> : kpi.percentChange < 0 ? <ArrowDown className="h-3 w-3" /> : null}
                                <span>{kpi.percentChange > 0 ? '+' : ''}{kpi.percentChange}%</span>
                              </span>
                              {yearStart && yearEnd ? (
                                <span className={styles["kpi-item__context"]}>
                                  {yearStart} → {yearEnd}
                                </span>
                              ) : null}
                            </div>
                          </TooltipTrigger>
                          {tooltipTitle && <TooltipContent>{tooltipTitle}</TooltipContent>}
                        </Tooltip>
                      </TooltipProvider>
                    );
                  })() : null}
                </div>
              ))}
            </div>
          )}

          {/* Insights */}
          {insights.length > 0 && (
            <div className={styles["insights-section"]}>
              <div className={styles["insights-section__header"]}>
                <h3 className="flex items-center gap-2"><Lightbulb className="h-5 w-5 text-yellow-500" /> Key Observations</h3>
              </div>
              <div className={styles["insights-section__list"]}>
                {insights.map((insight, idx) => {
                  const text = insight.text || "";
                  const statMatch = text.match(/([+-]?\d+(?:\.\d+)?%|\d+(?:\.\d+)?x)/i);
                  const stat = statMatch ? statMatch[1] : null;
                  let renderedText = text;
                  if (stat) {
                    const parts = text.split(stat);
                    renderedText = (
                      <>
                        {parts[0]}
                        <span className={styles["insight-highlight"]}>{stat}</span>
                        {parts[1]}
                      </>
                    );
                  }

                  return (
                    <div key={insight.id || idx} className={styles["insight-card"]}>
                      <div className={styles["insight-card__indicator"]} />
                      <div className={styles["insight-card__body"]}>
                        <div className={styles["insight-card__text"]}>
                          {renderedText}
                        </div>
                        {(insight.metadata || insight.subtext) && (
                          <div className={styles["insight-card__meta"]}>
                            {insight.metadata || insight.subtext}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Top Grid: Mix & Trends */}
          <div className={styles["tab-general__grid-row"]}>
            <Card className="h-full flex flex-col shadow-sm border-gray-200">
              <CardHeader className="pb-4">
                <CardTitle className="text-base font-semibold text-gray-800">Collaboration Scope</CardTitle>
                <CardDescription className="text-xs">Breakdown of publications by geographic and institutional reach</CardDescription>
              </CardHeader>
              <CardContent className="flex-1 pt-0">
                {enrichedScopeData.length > 0 ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 12, justifyContent: "space-between", height: "100%" }}>
                    <ChartContainer config={scopeChartConfig} className="flex-1 w-full max-h-[320px]">
                      <PieChart margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                        <Pie
                          data={enrichedScopeData}
                          cx="50%"
                          cy="50%"
                          innerRadius="40%"
                          outerRadius="60%"
                          strokeWidth={5}
                          paddingAngle={2}
                          dataKey="value"
                          nameKey="name"
                          labelLine={{ stroke: '#94a3b8', strokeWidth: 1 }}
                          label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(1)}%)`}
                        >
                          {enrichedScopeData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} stroke="none" />
                          ))}
                        </Pie>
                        <ChartTooltip content={<CustomPieTooltip metricLabel={currentMetric.label} total={scopeTotal} />} />
                      </PieChart>
                    </ChartContainer>
                    <div style={{ fontSize: 13, marginTop: 16, color: "#3d3d3d", textAlign: "left" }}>
                      {enrichedScopeData.map((item, idx) => (
                        <div key={idx} style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                          <span style={{ display: 'flex', alignItems: 'center' }}>
                            <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', backgroundColor: item.color, marginRight: 8 }}></span>
                            {item.name}
                          </span>
                          <strong>{item.value} {metric !== 'fwci' ? currentMetric.label : ""}</strong>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className={styles["no-data"]}>No collaboration mix data for the selected filters.</div>
                )}
              </CardContent>
            </Card>

            <Card className="h-full flex flex-col shadow-sm border-gray-200">
              <CardHeader className="pb-4">
                <CardTitle className="text-base font-semibold text-gray-800">Collaboration Trends ({currentMetric.label})</CardTitle>
                <CardDescription className="text-xs">Collaborative {currentMetric.label.toLowerCase()} over time</CardDescription>
              </CardHeader>
              <CardContent className="flex-1 pt-0">
                {trendData.length > 0 ? (
                  <ChartContainer config={chartConfig} className="h-full max-h-[420px] w-full">
                    <AreaChart data={trendData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="splitColorSolid" x1="0" y1="0" x2="1" y2="0">
                          <stop offset="0%" stopColor="var(--color-value)" stopOpacity={1} />
                          <stop offset={`${splitPercent}%`} stopColor="var(--color-value)" stopOpacity={1} />
                          <stop offset={`${splitPercent}%`} stopColor="var(--color-value)" stopOpacity={0} />
                          <stop offset="100%" stopColor="var(--color-value)" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="splitColorDashed" x1="0" y1="0" x2="1" y2="0">
                          <stop offset="0%" stopColor="var(--color-value)" stopOpacity={0} />
                          <stop offset={`${splitPercent}%`} stopColor="var(--color-value)" stopOpacity={0} />
                          <stop offset={`${splitPercent}%`} stopColor="var(--color-value)" stopOpacity={1} />
                          <stop offset="100%" stopColor="var(--color-value)" stopOpacity={1} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="year" tickLine={false} axisLine={false} tickMargin={8} />
                      <YAxis tickLine={false} axisLine={false} tickMargin={8} />
                      <ChartTooltip
                        cursor={false}
                        content={(props) => {
                          if (!props.payload || props.payload.length === 0) return null;
                          const deduplicatedPayload = [props.payload[0]];
                          return <ChartTooltipContent hideLabel {...props} payload={deduplicatedPayload} />;
                        }}
                      />
                      <Area type="monotone" dataKey="value" stroke="url(#splitColorSolid)" fill="var(--color-value)" fillOpacity={0.1} />
                      <Area type="monotone" dataKey="value" stroke="url(#splitColorDashed)" strokeDasharray="5 5" fill="none" />
                    </AreaChart>
                  </ChartContainer>
                ) : (
                  <div className={styles["no-data"]}>No trend data available for the selected date range.</div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Middle Grid: Geography, Subjects */}
          <div className={styles["tab-general__grid-row"]}>
            <Card className="h-full flex flex-col shadow-sm border-gray-200">
              <CardHeader className="pb-4">
                <CardTitle className="text-base font-semibold text-gray-800">{filters?.geography === "us" ? "US Reach" : "Global Reach"}</CardTitle>
                <CardDescription className="text-xs">{filters?.geography === "us" ? "Top collaborating states" : "Top collaborating countries"}</CardDescription>
              </CardHeader>
              <CardContent className="flex-1 pt-0">
                {enrichedReachData.length > 0 ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 12, justifyContent: "center", height: "100%" }}>
                    <ChartContainer config={chartConfig} className="h-full max-h-[420px] w-full">
                      <BarChart accessibilityLayer data={enrichedReachData.slice(0, 5)} layout="vertical" margin={{ left: 0, right: 10 }}>
                        <YAxis
                          dataKey="label"
                          type="category"
                          tickLine={false}
                          tickMargin={10}
                          axisLine={false}
                          width={130}
                          tick={{ fontSize: 12, fill: "var(--muted-foreground)" }}
                          tickFormatter={(value) => value.length > 20 ? `${value.substring(0, 17)}...` : value}
                        />
                        <XAxis dataKey="value" type="number" hide />
                        <ChartTooltip cursor={{ fill: 'transparent' }} content={<ChartTooltipContent hideLabel />} />
                        <Bar dataKey="value" layout="vertical" radius={5} barSize={24}>
                          {enrichedReachData.slice(0, 5).map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ChartContainer>
                  </div>
                ) : (
                  <div className={styles["no-data"]}>No current partners found for the selected criteria.</div>
                )}
              </CardContent>
            </Card>

            <Card className="h-full flex flex-col shadow-sm border-gray-200">
              <CardHeader className="pb-4">
                <CardTitle className="text-base font-semibold text-gray-800">Subject Areas</CardTitle>
                <CardDescription className="text-xs">Strongest collaborative fields</CardDescription>
              </CardHeader>
              <CardContent className="flex-1 pt-0">
                {subjectAreaData.length > 0 ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 12, justifyContent: "center", height: "100%" }}>
                    <ChartContainer config={chartConfig} className="h-full max-h-[420px] w-full">
                      <BarChart accessibilityLayer data={subjectAreaData.slice(0, 5)} layout="vertical" margin={{ left: 0, right: 10 }}>
                        <YAxis
                          dataKey="label"
                          type="category"
                          tickLine={false}
                          tickMargin={10}
                          axisLine={false}
                          width={130}
                          tick={{ fontSize: 12, fill: "var(--muted-foreground)" }}
                          tickFormatter={(value) => value.length > 25 ? `${value.substring(0, 22)}...` : value}
                        />
                        <XAxis dataKey="value" type="number" hide />
                        <ChartTooltip cursor={{ fill: 'transparent' }} content={<ChartTooltipContent hideLabel />} />
                        <Bar dataKey="value" layout="vertical" radius={5} barSize={24}>
                          {subjectAreaData.slice(0, 5).map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ChartContainer>
                  </div>
                ) : (
                  <div className={styles["no-data"]}>No subject area data available for the selected filters.</div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Social Impact (Institutions only) */}
          {isInstitution && (
            <div className={styles["tab-general__full-row"]}>
              <SocialImpactCard
                data={socialImpactData}
                isLoading={isLoadingSocialImpact}
                error={socialImpactError}
                onRetry={refetchSocialImpact}
                onSdgClick={setSelectedSdg}
              />
            </div>
          )}
        </>
      )}

      {/* SDG Detail Drawer */}
      {isInstitution && (
        <SdgDetailDrawer
          sdgNum={selectedSdg}
          isOpen={!!selectedSdg}
          onClose={() => setSelectedSdg(null)}
          fetchDetail={fetchSocialImpactDetail}
          rankLabel="SDG Rank"
          shareLabel="Share of Impact"
        />
      )}

    </div>
  );
};

export default TabGeneral;
